//================================================================
//  Component: Add Quote Pane 
//================================================================

//  Purpose: This side pane allows the user to add a quote line item

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the sidePane}
//    - setPaneOpen = { useState, updates the boolean value that opens and closes the sidePane}
//    - task = { useState, store the related parent Commercial task}
//    - currentQuoteReviewTask = { useState, used to store if there is a current quote review task}

//  Example:
//  
//    <AddQuotePane
//      paneOpen={paneOpen}
//      setPaneOpen={setPaneOpen}
//      task={task}
//      currentQuoteReviewTask={currentQuoteReviewTask}
//    ></AddQuotePane>

//================================================================

//Libraries
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../../../Library/WriteDocument';
import AddRemoveDocumentArray from '../../../../Library/AddRemoveDocumentArray';
import GetDocument from '../../../../Library/GetDocument';
import CreateRequest from '../../../../Library/CreateRequest';

//Images
import Loading from '../../../../Components/Images/Image_Loading_Ripple.svg';
import Error from '../../../../Components/Images/Icon_ErrorFatal_Red.svg';

//CSS

export default function AddQuotePane({
    paneOpen,
    setPaneOpen,
    task,
    currentQuoteReviewTask
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, handleSubmit, formState: { errors }, watch, reset, clearErrors } = useForm({
        defaultValues: {
            'quantity': 0,
            'unitprice': 0,
        }
    });

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to set pane status > 'onload', 'pending' 'error'
    const [paneStatus, setPaneStatus] = useState('onload');

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submit Handler 
    const onSubmit = (value) => {

        setPaneStatus('pending');

        // Calculate the quote total
        const quoteTotal = parseFloat((value.quantity * value.unitprice).toFixed(2));

        //-----------------------------------------------------------
        //  NEW Quote > Create new request & get assigned to field
        //-----------------------------------------------------------

        if (currentQuoteReviewTask?.quote?.length === 0 && currentQuoteReviewTask?.requestid === undefined) {

            const createRequestPromise = CreateRequest();
            let getDemandManagementTaskPromise;
            
            // If request origin is Demand Management Portal > Get demand management task to find assigned business partner
            if (task?.requestorigin === 'Demand') {
                getDemandManagementTaskPromise = GetDocument('requests', task?.parentrequestid);
            }

            // Write to Firestore
            return Promise.all([createRequestPromise, getDemandManagementTaskPromise])
            .then((results) => {

                const requestId = results[0][0];
                const demandManagementTask = results[1];

                let docPromise = [];

                // Create new quote review task
                const quoteTask = {
                    'requestid': requestId,
                    'parentrequestid': task?.parentrequestid,
                    'requestorigin': task?.requestorigin,
                    'poanumber': task?.poanumber,
                    'tasktype': 'Quote Review',
                    'taskname': 'Quote Review',
                    'applicationname': task?.applicationname ? task?.applicationname : '',
                    'initiativename': task?.initiativename ? task?.initiativename : '',
                    'status': 'Draft',
                    'assignmentgroup': '',
                    'requesttype': task?.requesttype,
                    'activityfeedvisible': false, // This will change to true once the status is 'For Review'
                    'approvalhubvisible': demandManagementTask === undefined ? true : false,
                    'rejectionreason': '',
                    'assignedto': demandManagementTask === undefined ? task?.createdby : demandManagementTask?.assignedto, // Check request origin and set to request ownwer OR assigned business partner
                    'assigneddate': new Date(),
                    'createddate': new Date(),
                    'createdby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'lastmodifieddate': new Date(),
                    'lastmodifiedby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'activityfeed': [
                        {
                            'activitydate': new Date(),
                            'actionedby': {
                                'email': getUser.emailaddress,
                                'givenname': getUser.givenname,
                                'surname': getUser.surname,
                            },
                            'action': 'created',
                            'comments': '',
                        },
                        {
                            'activitydate': new Date(Date.now() + 1000),
                            'actionedby': {
                                'email': demandManagementTask === undefined ? task?.createdby?.email : demandManagementTask?.assignedto?.email,
                                'givenname': demandManagementTask === undefined ? task?.createdby?.givenname : demandManagementTask?.assignedto?.givenname,
                                'surname': demandManagementTask === undefined ? task?.createdby?.surname : demandManagementTask?.assignedto?.surname
                            },
                            'action': 'assigned',
                            'comments': '',
                        }
                    ],
                    'quote': [
                        {
                            'description': value.description,
                            'quantity': value.quantity,
                            'unitprice': value.unitprice,
                            'currency': value.currency,
                            'total': quoteTotal,
                        }
                    ],
                    'comment': '',
                    'attachedfiles': []
                };

                docPromise.push(
                    WriteDocument('requests', requestId, quoteTask, true)
                );

                // Update commercial task to save the currency and total agreement amount
                const agreementData = {
                    'agreement':  {
                        'currency': value?.currency,
                        'totalagreementamount': (task?.agreement?.totalagreementamount === '' || task?.agreement?.totalagreementamount === undefined) ? 
                                                // Set as total agreement amount as quote total
                                                quoteTotal
                                                :
                                                // Add quote total to existing total agreement amount
                                                (task?.agreement?.totalagreementamount + quoteTotal),
                    }      
                };

                docPromise.push(
                    WriteDocument('requests', task?.requestid, agreementData, true)
                );

                // Resolve Promises
                return Promise.all(docPromise)
                .then(() => {

                    // Reset form and UI state
                    reset();
                    clearErrors();
                    setPaneStatus('onload');
                    setPaneOpen(false);

                })

            })
            .catch((error) => {

                console.error('error', error);
                setPaneStatus('error');

            })

        }

        //-----------------------------------------------------------
        //  EXISTING Quote > Update 'quote' total 
        //-----------------------------------------------------------

        else {

            // Update the quote array with new line item
            const newQuoteLineItem = {
                'description': value.description,
                'quantity': value.quantity,
                'unitprice': value.unitprice,
                'currency': value.currency === undefined ? task?.agreement?.currency : value.currency,
                'total': quoteTotal,
            };

            const lastmodifiedby = {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            };

            // Update commercial task to save the currency and total agreement amount
            const agreementData = {
                'agreement':  {
                    'currency': value.currency === undefined ? task?.agreement?.currency : value.currency,
                    'totalagreementamount': (task?.agreement?.totalagreementamount === '' || task?.agreement?.totalagreementamount === undefined) ? 
                                            // Set as total agreement amount as quote total
                                            quoteTotal
                                            :
                                            // Add quote total to existing total agreement amount
                                            task?.agreement?.totalagreementamount + quoteTotal,
                }      
            };

            //-----------------------------------------------------------
            //  If REJECTED Quote > Create new quote review request
            //----------------------------------------------------------- 

            if (currentQuoteReviewTask?.status === 'Rejected') {

                let finalPromises = [];

                return CreateRequest()
                .then((requestIds) => {

                    // Create new quote review task based on the current rejected quote totals (only update relevant fields)
                    currentQuoteReviewTask.requestid = requestIds[0];
                    currentQuoteReviewTask.status = 'Draft';
                    currentQuoteReviewTask.activityfeedvisible = false; // This will change to true once the status is 'For Review'
                    currentQuoteReviewTask.taskdescription = '';
                    currentQuoteReviewTask.rejectionreason = '';
                    currentQuoteReviewTask.assigneddate = new Date();
                    currentQuoteReviewTask.createddate = new Date();
                    currentQuoteReviewTask.createdby = {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    };
                    currentQuoteReviewTask.lastmodifieddate = new Date();
                    currentQuoteReviewTask.lastmodifiedby = lastmodifiedby;
                    currentQuoteReviewTask.activityfeed = [
                        {
                            'activitydate': new Date(),
                            'actionedby': {
                                'email': getUser.emailaddress,
                                'givenname': getUser.givenname,
                                'surname': getUser.surname,
                            },
                            'action': 'created',
                            'comments': '',
                        },
                        {
                            'activitydate': new Date(Date.now() + 1000),
                            'actionedby': {
                                'email': currentQuoteReviewTask?.assignedto?.email,
                                'givenname': currentQuoteReviewTask?.assignedto?.givenname,
                                'surname': currentQuoteReviewTask.assignedto?.surname
                            },
                            'action': 'assigned',
                            'comments': '',
                        }
                    ];
                    currentQuoteReviewTask.comment = '';
                    currentQuoteReviewTask.attachedfiles = [];
                    currentQuoteReviewTask.quote = [...currentQuoteReviewTask?.quote, newQuoteLineItem];

                    // Create new quote
                    finalPromises.push(
                        WriteDocument('requests', requestIds[0], currentQuoteReviewTask, true)
                    );
    
                    // Update total agreement amount and currency
                    finalPromises.push(
                        WriteDocument('requests', task?.requestid, agreementData, true)
                    );
    
                    return Promise.all(finalPromises)
                    .then(() => {
                        
                        // Reset form and UI state
                        reset();
                        clearErrors();
                        setPaneStatus('onload');
                        setPaneOpen(false);

                    })

                })
                .catch((error) => {

                    console.error('error', error);
                    setPaneStatus('error');

                })

            }

            //-----------------------------------------------------------
            //  Else DRAFT Quote > Update quote total in existing request
            //----------------------------------------------------------- 

            else {

                let finalPromises = [];

                finalPromises.push(
                    AddRemoveDocumentArray('requests', currentQuoteReviewTask?.requestid, 'quote', newQuoteLineItem, 'add', lastmodifiedby),
                );
                
                finalPromises.push(
                    WriteDocument('requests', task?.requestid, agreementData, true),
                );

                // Resolve Promises
                return Promise.all(finalPromises)
                .then(() => {

                    // Reset form and UI state
                    reset();
                    clearErrors();
                    setPaneStatus('onload');
                    setPaneOpen(false);

                })
                .catch((error) => {

                    console.error('error', error);
                    setPaneStatus('error');

                })  

            }       

        }

    }

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    // ==================================================
    //  Hide Side Pane
    // ==================================================

    if (!paneOpen) return null;

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    if (paneStatus === 'onload') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container'>

                    {/* ------------------------------- */}
                    {/*  Form                           */}
                    {/* ------------------------------- */}

                    <form className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> Add Cost to Quote</h4>
                        </div>

                        {/* Body */}
                        <div className='w-[50vw] h-full flex flex-col gap-3 overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/* ===================================== */}
                            {/*  Description                          */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> Description <span className='text-[#C4314B]'>*</span> </label>
                                <input
                                    className={errors.description ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    {...register('description', {
                                        required: 'This field is required',

                                    })}
                                    type='text'
                                    placeholder='E.g. Software licenses'
                                    style={{ width: '100%' }}
                                ></input>

                                {errors.description && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.description?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Quantity                             */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> Quantity <span className='text-[#C4314B]'>*</span> </label>
                                <input
                                    className={errors.quantity ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    {...register('quantity', {
                                        required: 'This field is required',
                                        pattern: {
                                            value: /^-?\d+(\.\d{1,2})?$/,
                                            message: 'Only numbers allowed (up to 2 decimal places)',
                                        },
                                        min: {
                                            value: 1,
                                            message: 'Minimum value is 1',
                                        },
                                    })}
                                    type='number'
                                    step='1'
                                    placeholder='Enter value'
                                ></input>

                                {errors.quantity && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.quantity?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Unit Price                           */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> 
                                    Unit Price <span className='text-[#C4314B]'>*</span>
                                    <br></br>
                                    <p className='font-light text-[15px] mt-2 mb-0'><i>Excluding Tax</i></p>
                                </label>
                                
                                <input
                                    className={errors.unitprice ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    {...register('unitprice', {
                                        required: 'This field is required',
                                        pattern: {
                                            value: /^-?\d+(\.\d{1,2})?$/,
                                            message: 'Only numbers allowed (up to 2 decimal places)',
                                        },
                                    })}
                                    type='number'
                                    step='0.01'
                                    placeholder='Enter value'
                                ></input>

                                {errors.unitprice && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.unitprice?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Currency                             */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'>Currency <span className='text-[#C4314B]'>*</span></label>

                                {/* Check if a quote line item exists and set the currency by default */}
                                {
                                    task?.agreement?.currency === '' ?
                                    
                                    // Editable Field
                                    <select
                                        className={errors.currency ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                                        {...register('currency', {
                                            required: 'This field is required'
                                        })}
                                        style={{ width: '450px' }}
                                        disabled={task?.agreement?.currency !== ''}
                                    >
                                        <option hidden value=''>-</option>
                                        <option value='AUD'> AUD </option>
                                        <option value='SGD'> SGD </option>
                                        <option value='GBP'> GBP </option>
                                        <option value='USD'> USD </option>
                                        <option value='EUR'> EUR </option>
                                        <option value='MYR'> MYR </option>
                                    </select>
                                    :
                                    // Disabled Field
                                    <select className='Input-Field-Select' style={{ width: '450px' }} disabled>
                                        <option>{task?.agreement?.currency}</option>
                                    </select>                                
                                }
                                

                                {errors.currency && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.currency?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Total                                */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> Total </label>
                                <input
                                    className='Input-Field-Text'
                                    type='number'
                                    disabled
                                    value={
                                        (watch('unitprice') || watch('quantity')) === 0 ? 
                                        0 
                                        : 
                                        (watch('unitprice') * watch('quantity')).toFixed(2)
                                    }
                                ></input>

                            </div>

                        </div>

                        {/* ------------------------------- */}
                        {/*  Submit Buttons                 */}
                        {/* ------------------------------- */}

                        <div className='flex flex-row gap-2 px-4 py-3'>

                            {/* Create */}
                            <button className='Primary-Button' type='submit' value='submit' disabled={errors.pricing || errors.currency || errors.comment ? true : false}>
                                Create
                            </button>

                            {/* Cancel */}
                            <button
                                className='Secondary-Button'
                                onClick={() => {

                                    // Reset Form and Close Side Pane
                                    setPaneOpen(false);
                                    reset();
                                    clearErrors();

                                }}
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </dialog>
            </div>
        );
    }

    // ---------------------------------------------------
    //  Pending
    // ---------------------------------------------------

    else if (paneStatus === 'pending') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container flex justify-center items-center text-center'>
                    <img alt='loading-circle-icon' src={Loading}></img>
                </dialog>
            </div>
        )
    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else if (paneStatus === 'error') {
        return (
            <div className='Pane-Background'>

                {/* Pane Container */}
                <dialog className='Pane-Container'>
                    <div className='flex flex-col gap-2 w-full justify-center items-center'>

                        <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
                        <h4 className='mb-0'>Oops! Something went wrong.</h4>
                        <p className='text-center leading-[1.7]'>
                            An error occurred while we processed your request.
                        </p>
                        <button
                            className='Primary-Button'
                            onClick={() => {

                                // Reset Form and Close Pane
                                setPaneOpen(false);
                                reset();
                                clearErrors();
                                setPaneStatus('onload');

                            }}
                        >
                            Close
                        </button>

                    </div>
                </dialog>
            </div>

        )
    }

    //------------------------------------------------------

}